import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, FormField, Heading, Input, Skeleton, Text, Textarea } from "@lendcord/concord";
import { isQueryApiError } from "@/app/store/baseApi";
import { useToast } from "@/app/toast";
import {
  useConfirmLogoMutation,
  useGetBrandProfileQuery,
  useLazyGetLogoDownloadUrlQuery,
  useRequestLogoUploadUrlMutation,
  useUpdateBrandProfileMutation,
  uploadLogoFile,
} from "../api/brandProfile.api";
import type { EnterpriseProfile, EnterpriseProfileRequest } from "../setup.types";

interface EnterpriseProfileFormState {
  legalName: string;
  displayName: string;
  registrationNumber: string;
  gstin: string;
  pan: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  tagline: string;
  brandColor: string;
  aboutBrand: string;
}

const EMPTY_FORM: EnterpriseProfileFormState = {
  legalName: "",
  displayName: "",
  registrationNumber: "",
  gstin: "",
  pan: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  tagline: "",
  brandColor: "#0E6B5C",
  aboutBrand: "",
};

// Mirrors EnterpriseProfileRequest's @Pattern validators (dto/request/EnterpriseProfileRequest.java)
// so the form catches an obviously-malformed value before round-tripping to the backend — the
// backend re-validates regardless, this is UX only.
const CIN_PATTERN = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const PHONE_PATTERN = /^\+?[0-9]{10,15}$/;
const PINCODE_PATTERN = /^[0-9]{6}$/;
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

type FormErrors = Partial<Record<keyof EnterpriseProfileFormState, string>>;

function toFormState(profile: EnterpriseProfile | undefined): EnterpriseProfileFormState {
  if (!profile) return EMPTY_FORM;
  return {
    legalName: profile.legalBusinessName ?? "",
    displayName: profile.displayName ?? "",
    registrationNumber: profile.registrationNumber ?? "",
    gstin: profile.gstin ?? "",
    pan: profile.pan ?? "",
    website: profile.website ?? "",
    contactEmail: profile.contactEmail ?? "",
    contactPhone: profile.contactPhone ?? "",
    addressLine1: profile.addressLine1 ?? "",
    addressLine2: profile.addressLine2 ?? "",
    city: profile.city ?? "",
    state: profile.state ?? "",
    pincode: profile.pincode ?? "",
    tagline: profile.tagline ?? "",
    brandColor: profile.brandColor ?? "#0E6B5C",
    aboutBrand: profile.about ?? "",
  };
}

function toRequestBody(form: EnterpriseProfileFormState): EnterpriseProfileRequest {
  return {
    legalBusinessName: form.legalName.trim() || undefined,
    displayName: form.displayName.trim() || undefined,
    registrationNumber: form.registrationNumber.trim() || undefined,
    gstin: form.gstin.trim() || undefined,
    pan: form.pan.trim() || undefined,
    website: form.website.trim() || undefined,
    contactEmail: form.contactEmail.trim() || undefined,
    contactPhone: form.contactPhone.trim() || undefined,
    addressLine1: form.addressLine1.trim() || undefined,
    addressLine2: form.addressLine2.trim() || undefined,
    city: form.city.trim() || undefined,
    state: form.state.trim() || undefined,
    pincode: form.pincode.trim() || undefined,
    brandColor: form.brandColor.trim() || undefined,
    tagline: form.tagline.trim() || undefined,
    about: form.aboutBrand.trim() || undefined,
  };
}

export function EnterpriseProfileTab() {
  const intl = useIntl();
  const { showToast } = useToast();
  const { data: profile, isLoading, isError } = useGetBrandProfileQuery();
  const [updateBrandProfile, { isLoading: isSaving }] = useUpdateBrandProfileMutation();
  const [requestLogoUploadUrl] = useRequestLogoUploadUrlMutation();
  const [confirmLogo] = useConfirmLogoMutation();
  const [fetchLogoDownloadUrl] = useLazyGetLogoDownloadUrlQuery();

  const [form, setForm] = useState<EnterpriseProfileFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [savedLogoUrl, setSavedLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Re-seed the form once the profile loads (or reloads after a save
  // elsewhere) — skipped while a local edit is in flight isn't a concern
  // here since this is the only editor for this data.
  useEffect(() => {
    if (profile) setForm(toFormState(profile));
  }, [profile]);

  // Fetch a fresh signed logo URL whenever the saved profile says a logo
  // exists and there's no locally-staged file preview taking precedence.
  useEffect(() => {
    if (!profile?.hasLogo || logoPreviewUrl) return;
    fetchLogoDownloadUrl()
      .unwrap()
      .then((result) => setSavedLogoUrl(result.url))
      .catch(() => setSavedLogoUrl(null));
  }, [profile?.hasLogo, logoPreviewUrl, fetchLogoDownloadUrl]);

  function updateField<K extends keyof EnterpriseProfileFormState>(key: K, value: EnterpriseProfileFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleLogoClick() {
    fileInputRef.current?.click();
  }

  async function handleLogoSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setLogoPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return URL.createObjectURL(file);
    });

    setIsUploadingLogo(true);
    try {
      const { storageKey, uploadUrl } = await requestLogoUploadUrl().unwrap();
      await uploadLogoFile(uploadUrl, file);
      await confirmLogo({ storageKey }).unwrap();
      showToast({
        status: "success",
        title: intl.formatMessage({ id: "lender.enterpriseProfile.logoUploadSuccessTitle" }),
      });
    } catch (error) {
      showToast({
        status: "error",
        title: intl.formatMessage({ id: "lender.enterpriseProfile.logoUploadErrorTitle" }),
        description: isQueryApiError(error)
          ? error.message
          : intl.formatMessage({ id: "lender.enterpriseProfile.logoUploadError" }),
      });
    } finally {
      setIsUploadingLogo(false);
    }
  }

  function validate(): boolean {
    const next: FormErrors = {};
    if (form.registrationNumber && !CIN_PATTERN.test(form.registrationNumber)) {
      next.registrationNumber = intl.formatMessage({ id: "lender.enterpriseProfile.registrationNumberError" });
    }
    if (form.gstin && !GSTIN_PATTERN.test(form.gstin)) {
      next.gstin = intl.formatMessage({ id: "lender.enterpriseProfile.gstinError" });
    }
    if (form.pan && !PAN_PATTERN.test(form.pan)) {
      next.pan = intl.formatMessage({ id: "lender.enterpriseProfile.panError" });
    }
    if (form.contactPhone && !PHONE_PATTERN.test(form.contactPhone)) {
      next.contactPhone = intl.formatMessage({ id: "lender.enterpriseProfile.contactPhoneError" });
    }
    if (form.pincode && !PINCODE_PATTERN.test(form.pincode)) {
      next.pincode = intl.formatMessage({ id: "lender.enterpriseProfile.pincodeError" });
    }
    if (form.brandColor && !HEX_COLOR_PATTERN.test(form.brandColor)) {
      next.brandColor = intl.formatMessage({ id: "lender.enterpriseProfile.brandColorError" });
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSave() {
    setFormError(undefined);
    if (!validate()) return;
    try {
      await updateBrandProfile(toRequestBody(form)).unwrap();
      showToast({ status: "success", title: intl.formatMessage({ id: "lender.enterpriseProfile.saveSuccessTitle" }) });
    } catch (error) {
      setFormError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.enterpriseProfile.saveError" }),
      );
    }
  }

  const logoSrc = logoPreviewUrl ?? savedLogoUrl;

  if (isLoading) {
    return (
      <div className="flex max-w-2xl flex-col gap-8">
        <Skeleton variant="rect" className="h-10 w-full" />
        <div className="flex flex-col gap-5">
          <Skeleton variant="text" className="h-6 w-40" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Skeleton variant="rect" className="h-16 w-full" />
            <Skeleton variant="rect" className="h-16 w-full" />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Skeleton variant="rect" className="h-16 w-full" />
            <Skeleton variant="rect" className="h-16 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <AlertBanner status="error">
        <FormattedMessage id="lender.enterpriseProfile.loadError" />
      </AlertBanner>
    );
  }

  return (
    <div className="flex max-w-2xl flex-col gap-8">
      <div className="flex flex-col gap-5">
        <Heading level={3}>
          <FormattedMessage id="lender.enterpriseProfile.companyDetailsHeading" />
        </Heading>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.legalNameLabel" })}>
            <Input
              value={form.legalName}
              onChange={(event) => updateField("legalName", event.target.value)}
              placeholder={intl.formatMessage({ id: "lender.enterpriseProfile.legalNamePlaceholder" })}
            />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.displayNameLabel" })}>
            <Input
              value={form.displayName}
              onChange={(event) => updateField("displayName", event.target.value)}
              placeholder={intl.formatMessage({ id: "lender.enterpriseProfile.displayNamePlaceholder" })}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.enterpriseProfile.registrationNumberLabel" })}
            error={errors.registrationNumber}
          >
            <Input
              value={form.registrationNumber}
              onChange={(event) => updateField("registrationNumber", event.target.value.toUpperCase())}
              placeholder="e.g. U65999BR2020PTC012345"
              maxLength={21}
            />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.gstinLabel" })} error={errors.gstin}>
            <Input
              value={form.gstin}
              onChange={(event) => updateField("gstin", event.target.value.toUpperCase())}
              placeholder="e.g. 10ABCDE1234F1Z5"
              maxLength={15}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.panLabel" })} error={errors.pan}>
            <Input
              value={form.pan}
              onChange={(event) => updateField("pan", event.target.value.toUpperCase())}
              placeholder="ABCDE1234F"
              maxLength={10}
            />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.websiteLabel" })}>
            <Input
              value={form.website}
              onChange={(event) => updateField("website", event.target.value)}
              placeholder="https://example.com"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.contactEmailLabel" })}>
            <Input
              type="email"
              value={form.contactEmail}
              onChange={(event) => updateField("contactEmail", event.target.value)}
              placeholder="contact@example.com"
            />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.enterpriseProfile.contactPhoneLabel" })}
            error={errors.contactPhone}
          >
            <Input
              value={form.contactPhone}
              onChange={(event) => updateField("contactPhone", event.target.value.replace(/\D/g, "").slice(0, 15))}
              placeholder="9876543210"
              inputMode="numeric"
            />
          </FormField>
        </div>

        <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.addressLine1Label" })}>
          <Input value={form.addressLine1} onChange={(event) => updateField("addressLine1", event.target.value)} />
        </FormField>
        <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.addressLine2Label" })}>
          <Input value={form.addressLine2} onChange={(event) => updateField("addressLine2", event.target.value)} />
        </FormField>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.cityLabel" })}>
            <Input value={form.city} onChange={(event) => updateField("city", event.target.value)} />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.stateLabel" })}>
            <Input value={form.state} onChange={(event) => updateField("state", event.target.value)} />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.pincodeLabel" })} error={errors.pincode}>
            <Input
              value={form.pincode}
              onChange={(event) => updateField("pincode", event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
            />
          </FormField>
        </div>
      </div>

      <div className="flex flex-col gap-5 border-t border-border pt-8">
        <Heading level={3}>
          <FormattedMessage id="lender.enterpriseProfile.brandProfileHeading" />
        </Heading>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleLogoClick}
            disabled={isUploadingLogo}
            className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-border bg-surface text-text-subtle hover:bg-accent-50 disabled:opacity-60"
            aria-label={intl.formatMessage({ id: "lender.enterpriseProfile.uploadLogoLabel" })}
          >
            {logoSrc ? (
              <img src={logoSrc} alt="" className="h-full w-full object-cover" />
            ) : (
              <Text size="xs" variant="muted" className="px-2 text-center">
                <FormattedMessage id="lender.enterpriseProfile.uploadLogoLabel" />
              </Text>
            )}
          </button>
          <div className="flex flex-col gap-1">
            <Button type="button" variant="secondary" density="compact" onClick={handleLogoClick} disabled={isUploadingLogo}>
              <FormattedMessage
                id={
                  isUploadingLogo
                    ? "lender.enterpriseProfile.uploadingLogoButton"
                    : logoSrc
                      ? "lender.enterpriseProfile.changeLogoButton"
                      : "lender.enterpriseProfile.uploadLogoButton"
                }
              />
            </Button>
            <Text size="xs" variant="muted">
              <FormattedMessage id="lender.enterpriseProfile.logoHint" />
            </Text>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoSelected} className="hidden" />
        </div>

        <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.brandColorLabel" })} error={errors.brandColor}>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={HEX_COLOR_PATTERN.test(form.brandColor) ? form.brandColor : "#0E6B5C"}
              onChange={(event) => updateField("brandColor", event.target.value)}
              className="h-10 w-14 shrink-0 cursor-pointer rounded-sm border border-border bg-surface-raised p-1"
              aria-label={intl.formatMessage({ id: "lender.enterpriseProfile.brandColorLabel" })}
            />
            <Input
              value={form.brandColor}
              onChange={(event) => updateField("brandColor", event.target.value)}
              className="max-w-[10rem]"
            />
          </div>
        </FormField>

        <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.taglineLabel" })}>
          <Input
            value={form.tagline}
            onChange={(event) => updateField("tagline", event.target.value)}
            placeholder={intl.formatMessage({ id: "lender.enterpriseProfile.taglinePlaceholder" })}
          />
        </FormField>

        <FormField label={intl.formatMessage({ id: "lender.enterpriseProfile.aboutBrandLabel" })}>
          <Textarea
            value={form.aboutBrand}
            onChange={(event) => updateField("aboutBrand", event.target.value)}
            rows={4}
            maxLength={1000}
            placeholder={intl.formatMessage({ id: "lender.enterpriseProfile.aboutBrandPlaceholder" })}
          />
        </FormField>
      </div>

      {formError && <AlertBanner status="error">{formError}</AlertBanner>}

      <div>
        <Button type="button" onClick={handleSave} disabled={isSaving}>
          <FormattedMessage id={isSaving ? "lender.enterpriseProfile.savingButton" : "lender.enterpriseProfile.saveButton"} />
        </Button>
      </div>
    </div>
  );
}
